import { computed, ref } from 'vue';
import type { UserProfile } from '@/lib/api';
import { fetchCurrentUser } from '@/lib/apiClient';

const TOKEN_KEY = 'hp_token';
const USER_KEY = 'hp_user';

const token = ref<string | null>(localStorage.getItem(TOKEN_KEY));
const user = ref<UserProfile | null>(readStoredUser());

function readStoredUser(): UserProfile | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as UserProfile;
  } catch {
    return null;
  }
}

export function useAuth() {
  const isAuthenticated = computed(() => Boolean(token.value));
  const isAdmin = computed(() => user.value?.role === 'admin');
  const isBirthdayPerson = computed(() => user.value?.role === 'birthday_person');

  function setSession(nextToken: string, nextUser: UserProfile) {
    token.value = nextToken;
    user.value = nextUser;
    localStorage.setItem(TOKEN_KEY, nextToken);
    localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
  }

  function updateUser(nextUser: UserProfile, nextToken?: string) {
    user.value = nextUser;
    localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
    if (nextToken) {
      token.value = nextToken;
      localStorage.setItem(TOKEN_KEY, nextToken);
    }
  }

  function clearSession() {
    token.value = null;
    user.value = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  async function restoreSession() {
    if (!token.value) return false;

    try {
      const response = await fetchCurrentUser(token.value);
      user.value = response.user;
      localStorage.setItem(USER_KEY, JSON.stringify(response.user));
      return true;
    } catch {
      clearSession();
      return false;
    }
  }

  return {
    token,
    user,
    isAuthenticated,
    isAdmin,
    isBirthdayPerson,
    setSession,
    updateUser,
    clearSession,
    restoreSession,
  };
}
